import { useCallback, useMemo } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  DataGridV2,
  type DataGridV2Column,
  type DataGridV2QuickFilter,
  type DataGridV2RowAction,
} from '@bloomays-lib/ui.shared/datagrid';
import { InformationMessage } from '@bloomays-lib/ui.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import {
  accountManagerInitials,
  formatAccountManagerShort,
  formatMissionPeriod,
  monthDateForKey,
  monthlyEstimate,
  MissingMonthKey,
  MissingPurchaseOrderRow,
  MissingUrgency,
  MissionEnrichment,
} from '../../helpers/purchaseOrderDashboard';
import { MISSING_URGENCY_TONE } from '../../helpers/purchaseOrderSemanticStatus';

export type MissingPurchaseOrderTabProps = {
  missingRows: MissingPurchaseOrderRow[];
  missionEnrichmentByRecordId: Record<string, MissionEnrichment>;
  referenceDate: Date;
  onCreate: (row: MissingPurchaseOrderRow) => void;
  requestedMonthFilter?: MissingMonthKey;
  onRequestedMonthFilterChange?: (month: MissingMonthKey | undefined) => void;
};

type MissingViewRow = {
  id: string;
  missionLabel: string;
  missionPeriod: string;
  clientName: string;
  bloomerName: string;
  amShort: string;
  amInitials: string;
  missingMonthLabel: string;
  missingMonth: MissingMonthKey;
  tjm?: number;
  monthlyEstimate?: number;
  urgency: MissingUrgency;
  raw: MissingPurchaseOrderRow;
};

const formatAmount = (value: number | undefined): string => {
  if (value === undefined) {
    return '—';
  }
  return `${value.toLocaleString('fr-FR')} €`;
};

export const MissingPurchaseOrderTab = ({
  missingRows,
  missionEnrichmentByRecordId,
  referenceDate,
  onCreate,
  requestedMonthFilter,
  onRequestedMonthFilterChange,
}: MissingPurchaseOrderTabProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);

  const urgencyLabel = useMemo<Record<MissingUrgency, string>>(
    () => ({
      urgent: t('urgencyUrgent'),
      anticipate: t('urgencyAnticipate'),
      plan: t('urgencyPlan'),
    }),
    [t],
  );

  const rows = useMemo<MissingViewRow[]>(() => {
    return missingRows.map((row) => {
      const enrichment = missionEnrichmentByRecordId[row.missionRecordId];
      const monthDate = monthDateForKey(referenceDate, row.missingMonth);
      const monthShort = convertDateToStringFormat(monthDate, 'MMM yy');
      const accountManager = enrichment?.accountManager;
      return {
        id: row.id,
        missionLabel: row.missionLabel,
        missionPeriod: formatMissionPeriod(enrichment?.beginMission, enrichment?.endMission),
        clientName: enrichment?.clientSocietyName ?? row.clientSocietyName,
        bloomerName: enrichment?.bloomerFullName ?? '—',
        amShort: formatAccountManagerShort(accountManager),
        amInitials: accountManagerInitials(accountManager),
        missingMonthLabel: t('monthMissingNoBdc', { month: monthShort }),
        missingMonth: row.missingMonth,
        tjm: enrichment?.tjm,
        monthlyEstimate: monthlyEstimate(enrichment?.tjm, monthDate),
        urgency: row.urgency,
        raw: row,
      };
    });
  }, [missingRows, missionEnrichmentByRecordId, referenceDate, t]);

  const quickFilterValues = useMemo(() => ({ missingMonth: requestedMonthFilter ?? '' }), [requestedMonthFilter]);

  const handleQuickFilterValuesChange = useCallback(
    (values: Record<string, string>) => {
      const month = values.missingMonth;
      onRequestedMonthFilterChange?.(month ? (month as MissingMonthKey) : undefined);
    },
    [onRequestedMonthFilterChange],
  );

  const monthQuickFilter = useMemo<DataGridV2QuickFilter<MissingViewRow>>(
    () => ({
      field: 'missingMonth',
      label: t('colMissingMonth'),
      display: 'inline',
      allLabel: t('filterAll'),
      options: [
        { value: 'monthMinus1', label: t('filterMonthMinus1'), tone: 'danger' },
        { value: 'current', label: t('filterCurrentMonth'), tone: 'danger' },
        { value: 'monthPlus1', label: t('filterM1'), tone: 'danger' },
        { value: 'monthPlus2', label: t('filterM2'), tone: 'warning' },
        { value: 'monthPlus3', label: t('filterM3'), tone: 'info' },
      ],
      predicate: (row, value) => row.missingMonth === value,
    }),
    [t],
  );

  const buildRowActions = useCallback(
    (row: MissingViewRow): DataGridV2RowAction[] => [
      {
        label: t('createPurchaseOrder'),
        icon: <AddCircleOutlineIcon fontSize="small" />,
        onClick: () => onCreate(row.raw),
      },
    ],
    [onCreate, t],
  );

  const columns = useMemo<DataGridV2Column<MissingViewRow>[]>(
    () => [
      {
        field: 'missionLabel',
        header: t('colMission'),
        minWidth: 220,
        flex: 1,
        searchText: (row) => `${row.missionLabel} ${row.clientName} ${row.bloomerName} ${row.amShort}`,
        render: (row) => (
          <CellStack>
            <Typography variant="body2" fontWeight={600}>
              {row.missionLabel}
            </Typography>
            <SecondaryText variant="caption">{row.missionPeriod}</SecondaryText>
          </CellStack>
        ),
      },
      {
        field: 'clientName',
        header: t('colClientBloomer'),
        minWidth: 200,
        flex: 1,
        render: (row) => (
          <CellStack>
            <Typography variant="body2" fontWeight={600}>
              {row.clientName}
            </Typography>
            <SecondaryText variant="caption">{row.bloomerName}</SecondaryText>
          </CellStack>
        ),
      },
      {
        field: 'amShort',
        header: t('colAm'),
        width: 140,
        variant: 'avatar',
        avatar: (row) => ({
          initials: row.amInitials,
          name: row.amShort,
        }),
      },
      { field: 'missingMonthLabel', header: t('colMissingMonth'), minWidth: 180 },
      {
        field: 'tjm',
        header: t('colTjmEstimate'),
        minWidth: 200,
        render: (row) => (
          <CellStack>
            <Typography variant="body2">{t('tjmPerDay', { amount: formatAmount(row.tjm) })}</Typography>
            <SecondaryText variant="caption">
              {t('estimatePerMonth', { amount: formatAmount(row.monthlyEstimate) })}
            </SecondaryText>
          </CellStack>
        ),
      },
      {
        field: 'urgency',
        header: t('colUrgency'),
        minWidth: 160,
        variant: 'chip',
        chip: (row) => ({
          label: urgencyLabel[row.urgency],
          tone: MISSING_URGENCY_TONE[row.urgency],
          dot: true,
        }),
      },
      {
        field: 'actions',
        header: t('colActions'),
        width: 120,
        variant: 'actions',
        actions: buildRowActions,
      },
    ],
    [buildRowActions, t, urgencyLabel],
  );

  return (
    <div>
      <BannerBox>
        <InformationMessage text={t('missingBanner')} />
        <SecondaryText variant="caption">{t('missingHorizon')}</SecondaryText>
      </BannerBox>

      {rows.length === 0 ? (
        <EmptyText variant="body1Light">{t('emptyMissing')}</EmptyText>
      ) : (
        <DataGridV2<MissingViewRow>
          uniqueId="purchase-orders-missing-v2"
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          quickFilters={[monthQuickFilter]}
          quickFilterValues={onRequestedMonthFilterChange ? quickFilterValues : undefined}
          onQuickFilterValuesChange={onRequestedMonthFilterChange ? handleQuickFilterValuesChange : undefined}
          search
          searchPlaceholder={t('searchMissingPlaceholder')}
          pagination
          pageSize={25}
          getRowTone={(row) => MISSING_URGENCY_TONE[row.urgency]}
        />
      )}
    </div>
  );
};

export default MissingPurchaseOrderTab;

const CellStack = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 2,
}));

const SecondaryText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#6b7280',
}));

const BannerBox = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginBottom: 16,
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#374151',
  fontStyle: 'italic',
  marginTop: 16,
}));
